import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { component, ComponentPolicy, field, ObjectManager, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { Physical, PotentialInteraction } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { PreferredTextColor, TextAlignment } from '../../canvas/entities/TextConventions';
import { FieldLayout } from '../../canvas/fields/AdaptableField';
import { TextField } from '../../canvas/fields/TextField';
import { checkboxPropertyLayout } from './CheckboxPropertyLayout';

const invocation = defineCommand(
    'checkboxInvoke',
    'Checkbox Invoke',
    (r, [id]: [string]) => {

        const objects = r.call(ObjectManager);
        const self = objects.get(id, Checkbox);

        objects.update(() => {
            self.value = !self.value;
        });
        // const script = CommandScript.parse(self.commandScript);
        // const [ok, error] = interpreter.verify(script);

        // if (ok) {
        //     interpreter.exec(script);
        // }
        // else {
        //     // eslint-disable-next-line no-console
        //     console.error(error);
        // }
    },
    [],
);

const mutation = defineCommand(
    'buttonMutate',
    'Button Mutate',
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const checkboxDef = {
    name: 'Checkbox',
    implements: [Physical, Resizable, Snappable, PreferredTextColor],
    invocation,
    mutation,
    propertyLayout: checkboxPropertyLayout,
};

const checkboxPolicy = ComponentPolicy.noChildren('Checkbox');

class CheckboxText extends TextField {

    // Overrides
    @property({ type: 'number', default: 22 })
    public fontSize!: number;

    public readonly fontFamily = 'Cabin';
    public readonly fontColor = 'inherit';
    public readonly fontWeight = 500;
    public readonly fontStyle = 'normal';
    public readonly textAlignH = TextAlignment.start;
    public readonly textAlignV = TextAlignment.start;
    public readonly textMargin = 10;
    public readonly lineHeight = 1.25;
    public readonly maxFontSize = null;
    public readonly maxLength = null;

    @property({ type: 'string', default: 'My Checkbox' })
    public value!: string;

    public get boundary() {
        return true;
    }

    public get layout() {
        return this.owner.cast(Checkbox).layout.text;
    }

    public get shouldSnap() {
        return true;
    }
}

export const CheckboxLayout = (object: Checkbox) => {
    const { width, height } = object;

    const fontSize = object.get('text|fontSize');

    const base = new Rect(0, 0, width, height);
    const value = new Rect(0, 0, fontSize * 2, fontSize * 2);
    const text = new FieldLayout(new Rect(value.right + 10, 0, width - value.right + 10, height), 2);

    return Object.freeze({ width, height, value, text, });
};

@component(checkboxDef)
export class Checkbox extends RectangularPhysical implements Snappable {
    shouldSnap: boolean;

    @property({ type: 'string', default: 'white' })
    public background!: string;

    @field({ type: CheckboxText, valueOnly: true })
    public text!: string;

    @property({ type: 'boolean', default: false })
    public value!: boolean;

    // @property({ type: 'string', default: '#ffffff' })
    // public background!: string;

    public get minWidth() {
        return Number(this.get('text|fontSize')) * 2;
    }

    public get minHeight() {
        return Number(this.get('text|fontSize')) * 2;
    }

    public get boundary() {
        return true;
    }

    @memoUsing('ticker', 'children')
    public get layout() {
        return CheckboxLayout(this);
    }

    protected get policy() {
        return checkboxPolicy;
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        if (this.localBounds.contains(this.toRelative(mpt))) {
            if (type === 'invoke') {
                return [false, 'none'];
            }
            else {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }
}
